import { createServerFn } from "@tanstack/react-start";
import {
  type StripeEnv,
  createStripeClient,
  getStripeErrorMessage,
} from "@/lib/stripe.server";
import { validateTexasZip, normalizeZip } from "@/lib/texas-zip";
import {
  SHOP_PRICES_CENTS,
  calculateTotals,
  isSoldOut,
  PICKUP_LOCATION,
  TAX_RATE,
} from "@/lib/shop-pricing";

export type ShopCheckoutItem = {
  productId: string;
  name: string;
  quantity: number;
  notes?: string;
};

export type ShopCheckoutInput = {
  items: ShopCheckoutItem[];
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  street?: string;
  city: string;
  state: string;
  /** 5-digit shipping ZIP; must be a Texas ZIP for shipped orders. */
  zip?: string;
  /** "pickup" collects the order at our Seguin, TX shop (no shipping charge). */
  fulfillment?: "ship" | "pickup";
  orderNotes?: string;
  returnUrl: string;
  environment: StripeEnv;
};

type CheckoutSessionResult = { clientSecret: string } | { error: string };

export const createShopCheckoutSession = createServerFn({ method: "POST" })
  .inputValidator((data: ShopCheckoutInput) => {
    if (!Array.isArray(data.items) || data.items.length === 0) {
      throw new Error("Your cart is empty.");
    }
    if (data.items.length > 40) throw new Error("Too many items in one order.");
    if (data.state !== "TX") {
      throw new Error(
        "Texas Made Shade can only sell and ship within the state of Texas.",
      );
    }
    if (!data.customerName?.trim() || !data.customerEmail?.trim()) {
      throw new Error("Name and email are required.");
    }
    if (data.fulfillment && data.fulfillment !== "ship" && data.fulfillment !== "pickup") {
      throw new Error("Choose shipping or local pickup.");
    }
    if (data.fulfillment !== "pickup" && !data.city?.trim()) {
      throw new Error("Shipping city is required.");
    }
    // Authoritative Texas gate: the ZIP decides, not the typed city name.
    if (data.fulfillment !== "pickup") {
      const zipMsg = validateTexasZip(data.zip ?? "");
      if (zipMsg) throw new Error(zipMsg);
    }
    for (const item of data.items) {
      if (isSoldOut(item.productId)) {
        throw new Error(
          `${item.name || item.productId} is currently sold out. Remove it and request a quote so we can let you know when it's back.`,
        );
      }
      if (typeof SHOP_PRICES_CENTS[item.productId] !== "number") {
        throw new Error(`${item.name || item.productId} is quote-only.`);
      }
      if (
        !Number.isFinite(item.quantity) ||
        item.quantity < 1 ||
        item.quantity > 50
      ) {
        throw new Error("Quantity must be between 1 and 50.");
      }
    }
    return data;
  })
  .handler(async ({ data }): Promise<CheckoutSessionResult> => {
    // Server recomputes every amount from our own price table; client totals
    // are display only and are never trusted.
    const lines = data.items.map((item) => ({
      productId: item.productId,
      quantity: Math.floor(item.quantity),
    }));
    const isPickup = data.fulfillment === "pickup";
    const totals = calculateTotals(lines, { pickup: isPickup });

    const { supabaseAdmin: admin } = await import("@/integrations/supabase/client.server");
    const supabaseAdmin = admin as any;

    const shippingAddress = isPickup
      ? `Local pickup — ${PICKUP_LOCATION}`
      : [
          data.street?.trim(),
          `${data.city.trim()}, TX ${normalizeZip(data.zip ?? "")}`.trim(),
        ]
          .filter(Boolean)
          .join(" — ") || null;

    const { data: order, error: orderError } = await supabaseAdmin
      .from("store_orders")
      .insert({
        environment: data.environment,
        status: "pending",
        customer_name: data.customerName.trim(),
        customer_email: data.customerEmail.trim(),
        customer_phone: data.customerPhone?.trim() || null,
        shipping_address: shippingAddress,
        shipping_state: "TX",
        items: data.items.map((item) => ({
          product_id: item.productId,
          name: item.name,
          quantity: Math.floor(item.quantity),
          unit_price_cents: SHOP_PRICES_CENTS[item.productId],
          notes: item.notes || null,
        })),
        subtotal_cents: totals.subtotalCents,
        shipping_cents: totals.shippingCents,
        tax_cents: 0,
        total_cents: totals.totalCents,
        order_notes:
          [
            isPickup ? "LOCAL PICKUP at our Seguin, TX shop (no shipping)." : null,
            data.orderNotes?.trim() || null,
          ]
            .filter(Boolean)
            .join("\n") || null,
      })
      .select("id")
      .single();

    if (orderError || !order) {
      return { error: "We couldn't start your order. Please try again." };
    }

    try {
      const stripe = createStripeClient(data.environment);

      // Tangible-goods tax code so the provider classifies these products and
      // can compute the exact rate for the buyer's Texas delivery address.
      const GOODS_TAX_CODE = "txcd_99999999";
      const SHIPPING_TAX_CODE = "txcd_92010001";

      const nameByProductId = new Map(
        data.items.map((item) => [item.productId, item.name]),
      );

      const lineItems = lines.map((line) => ({
        price_data: {
          currency: "usd",
          product_data: {
            name: nameByProductId.get(line.productId) || line.productId,
            tax_code: GOODS_TAX_CODE,
          },
          unit_amount: SHOP_PRICES_CENTS[line.productId],
          // Prices are entered exclusive of tax; tax is added at checkout.
          tax_behavior: "exclusive" as const,
        },
        quantity: line.quantity,
      }));

      const session = await stripe.checkout.sessions.create({
        line_items: lineItems,
        mode: "payment",
        ui_mode: "embedded_page",
        return_url: data.returnUrl,
        customer_email: data.customerEmail.trim(),
        // Required so the provider can determine the exact Texas rate.
        billing_address_collection: "required",
        automatic_tax: { enabled: true },
        ...(!isPickup && {
          shipping_address_collection: { allowed_countries: ["US"] as const },
        }),
        ...(totals.shippingCents > 0 && {
          shipping_options: [
            {
              shipping_rate_data: {
                type: "fixed_amount" as const,
                display_name: "Flat rate — Texas delivery",
                fixed_amount: { amount: totals.shippingCents, currency: "usd" },
                tax_behavior: "exclusive" as const,
                tax_code: SHIPPING_TAX_CODE,
              },
            },
          ],
        }),
        payment_intent_data: {
          description: isPickup
            ? "Texas Made Shade shop order — local pickup (Seguin, TX)"
            : "Texas Made Shade shop order",
        },
        metadata: {
          order_id: order.id,
          shop_order: "true",
          fulfillment: isPickup ? "pickup" : "ship",
        },
      });


      await supabaseAdmin
        .from("store_orders")
        .update({ stripe_session_id: session.id })
        .eq("id", order.id);

      return { clientSecret: session.client_secret ?? "" };
    } catch (error) {
      await supabaseAdmin
        .from("store_orders")
        .update({ status: "failed" })
        .eq("id", order.id);
      return { error: getStripeErrorMessage(error) };
    }
  });
